/**
 * Input validation for indexer requests
 *
 * Validates chain IDs, addresses, and paths before making requests
 * to prevent malformed data from reaching the indexer.
 */

import { isAddress } from 'viem'
import { InvalidAddressError, InvalidChainIdError, InvalidPathError } from './errors'

/** Valid chain IDs that the indexer supports */
export const VALID_CHAIN_IDS = [999] as const // HyperEVM

/** Maximum allowed path length to prevent abuse */
const MAX_PATH_LENGTH = 256

/** Allowed path prefixes */
const ALLOWED_PATH_PREFIXES = ['/api/chains/', '/api/referrals/', '/api/health', '/health'] as const

/** Paths that are known to be expensive and should have extra validation */
export const EXPENSIVE_PATHS = ['/pooled/', '/isolated/', '/users/'] as const

/**
 * Validates that a chain ID is supported
 */
export function validateChainId(chainId: number | string): number {
  const numericChainId = typeof chainId === 'string' ? Number.parseInt(chainId, 10) : chainId

  if (Number.isNaN(numericChainId)) {
    throw new InvalidChainIdError(chainId, [...VALID_CHAIN_IDS])
  }

  if (!VALID_CHAIN_IDS.includes(numericChainId as (typeof VALID_CHAIN_IDS)[number])) {
    throw new InvalidChainIdError(numericChainId, [...VALID_CHAIN_IDS])
  }

  return numericChainId
}

/**
 * Validates that an address is a valid Ethereum address
 */
export function validateAddress(address: string): string {
  if (!address || typeof address !== 'string') {
    throw new InvalidAddressError(address ?? 'undefined')
  }

  // Normalize to lowercase
  const normalized = address.toLowerCase()

  // Check format: 0x + 40 hex characters
  if (!/^0x[a-f0-9]{40}$/.test(normalized)) {
    throw new InvalidAddressError(address)
  }

  // Use viem's isAddress for additional validation
  if (!isAddress(address)) {
    throw new InvalidAddressError(address)
  }

  return normalized
}

/**
 * Validates and sanitizes a path for the indexer
 */
export function validatePath(path: string): string {
  if (!path || typeof path !== 'string') {
    throw new InvalidPathError(path ?? 'undefined', 'Path is required')
  }

  // Check length
  if (path.length > MAX_PATH_LENGTH) {
    throw new InvalidPathError(path, `Path exceeds maximum length of ${MAX_PATH_LENGTH}`)
  }

  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`

  // Check for path traversal attempts
  if (normalizedPath.includes('..') || normalizedPath.includes('//')) {
    throw new InvalidPathError(path, 'Path contains invalid characters')
  }

  // Check for allowed prefixes
  const isAllowed = ALLOWED_PATH_PREFIXES.some((prefix) => normalizedPath.startsWith(prefix))
  if (!isAllowed) {
    throw new InvalidPathError(path, `Path must start with one of: ${ALLOWED_PATH_PREFIXES.join(', ')}`)
  }

  return normalizedPath
}

/**
 * Extracts and validates parameters from a path like /api/chains/{chainId}/pooled/{address}/apy
 */
export function extractAndValidatePathParams(path: string): {
  chainId?: number
  address?: string
  pathType: 'pooled' | 'isolated' | 'users' | 'referrals' | 'health' | 'other'
} {
  const normalizedPath = validatePath(path)

  // Extract chain ID if present
  const chainIdMatch = normalizedPath.match(/\/api\/chains\/(\d+)/)
  const chainId = chainIdMatch ? validateChainId(chainIdMatch[1]!) : undefined

  // Extract address if present (for pooled, isolated, or users endpoints).
  // Aggregate endpoints like /api/chains/{chainId}/pooled/apy do not include an address.
  const addressMatch = normalizedPath.match(/\/(pooled|isolated|users)\/(0x[a-fA-F0-9]{40})(?:\/|$)/)
  const address = addressMatch ? validateAddress(addressMatch[2]!) : undefined

  // Determine path type
  let pathType: 'pooled' | 'isolated' | 'users' | 'referrals' | 'health' | 'other' = 'other'
  if (normalizedPath.includes('/pooled/')) pathType = 'pooled'
  else if (normalizedPath.includes('/isolated/')) pathType = 'isolated'
  else if (normalizedPath.includes('/users/')) pathType = 'users'
  else if (normalizedPath.includes('/referrals/')) pathType = 'referrals'
  else if (normalizedPath.includes('/health')) pathType = 'health'

  return { chainId, address, pathType }
}

/**
 * Checks if a path is an expensive operation that should be rate-limited more strictly
 */
export function isExpensivePath(path: string): boolean {
  return EXPENSIVE_PATHS.some((prefix) => path.includes(prefix))
}
